import React, { useState } from "react";
import { Box, ButtonBase, Collapse, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import UserAvatar from "../../components/UserAvatar";
import type { ConfigAuditChange, ConfigAuditEntry } from "../../apis/configAuditApi";
import { FONT, NEUTRAL, RADIUS, VIOLET } from "../../styles/brandTokens";
import { actionLabel, entityTypeLabel, formatChangeValue, humanizeField, timeLabel } from "./configAuditFormat";

const DANGER = "#B42318";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const REMOVING_ACTIONS = new Set(["delete", "disable", "disconnect", "cancel"]);
const ADDING_ACTIONS = new Set(["create", "enable", "connect", "grant"]);

function actionColor(action: string): string {
  if (REMOVING_ACTIONS.has(action)) return DANGER;
  if (ADDING_ACTIONS.has(action)) return VIOLET.main;
  return NEUTRAL.textMuted;
}

/** State as a 7px dot and text with no container: the house style for statuses. */
function ActionMark({ action }: { action: string }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: actionColor(action), flexShrink: 0 }} />
      <Typography
        sx={{ fontFamily: FONT.sans, fontSize: 12.5, fontWeight: 600, color: REMOVING_ACTIONS.has(action) ? DANGER : NEUTRAL.text }}
      >
        {actionLabel(action)}
      </Typography>
    </Box>
  );
}

const cellSx = {
  fontFamily: FONT.sans,
  fontSize: 12.5,
  py: 0.75,
  px: 1.25,
  borderTop: `1px solid ${NEUTRAL.border}`,
  wordBreak: "break-word" as const,
  whiteSpace: "pre-wrap" as const,
  verticalAlign: "top",
};

interface ValueCellProps {
  value: unknown;
  redacted?: boolean;
  side: "before" | "after";
}

function ValueCell({ value, redacted, side }: ValueCellProps) {
  const empty = value === null || value === undefined;
  if (redacted && !empty) {
    return (
      <Box component="td" sx={{ ...cellSx, color: NEUTRAL.textMuted, fontStyle: "italic" }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          <LockOutlinedIcon sx={{ fontSize: 13 }} aria-hidden />
          {side === "after" ? "changed · value hidden" : "value hidden"}
        </Box>
      </Box>
    );
  }
  return (
    <Box component="td" sx={{ ...cellSx, color: empty ? NEUTRAL.textMuted : NEUTRAL.text, fontFamily: empty ? FONT.sans : MONO }}>
      {formatChangeValue(value)}
    </Box>
  );
}

interface ChangeTableProps {
  changes: ConfigAuditChange[];
  source: ConfigAuditEntry["change_source"];
}

function ChangeTable({ changes, source }: ChangeTableProps) {
  if (changes.length === 0) {
    return (
      <Typography sx={{ fontFamily: FONT.sans, fontSize: 12.5, color: NEUTRAL.textMuted, px: 1.25, py: 1 }}>
        No setting-level details were recorded for this change.
      </Typography>
    );
  }
  return (
    <Box>
      {source === "request" && (
        <Typography sx={{ fontFamily: FONT.sans, fontSize: 11.5, color: NEUTRAL.textMuted, px: 1.25, pb: 0.75 }}>
          The saved settings could not be read back, so these are the values that were submitted.
        </Typography>
      )}
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <Box component="thead">
          <Box component="tr">
            {["Setting", "Before", "After"].map((heading) => (
              <Box
                key={heading}
                component="th"
                scope="col"
                sx={{
                  ...cellSx,
                  borderTop: 0,
                  textAlign: "left",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: NEUTRAL.textMuted,
                }}
              >
                {heading}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {changes.map((change) => (
            <Box component="tr" key={change.field}>
              <Box component="td" sx={{ ...cellSx, color: NEUTRAL.textSecondary }} title={change.field}>
                {humanizeField(change.field)}
              </Box>
              <ValueCell value={change.before} redacted={change.redacted} side="before" />
              <ValueCell value={change.after} redacted={change.redacted} side="after" />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

interface ChangeLogEntryProps {
  entry: ConfigAuditEntry;
}

export default function ChangeLogEntry({ entry }: ChangeLogEntryProps) {
  const [open, setOpen] = useState(false);
  const detailsId = `config-change-${entry.id}`;
  const fieldCount = entry.changes.length;
  const actor = entry.actor_email || "Unknown user";

  return (
    <Box
      component="li"
      sx={{
        listStyle: "none",
        borderTop: `1px solid ${NEUTRAL.border}`,
        "&:first-of-type": { borderTop: 0 },
        bgcolor: open ? NEUTRAL.subtle : NEUTRAL.surface,
        transition: "background-color 150ms ease",
      }}
    >
      <ButtonBase
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={detailsId}
        sx={{
          width: "100%",
          textAlign: "left",
          display: "grid",
          gridTemplateColumns: { xs: "1fr auto", md: "84px 230px 112px minmax(0, 1fr) auto" },
          alignItems: "center",
          columnGap: 2,
          px: 2,
          py: 1.25,
          "&:hover": { bgcolor: NEUTRAL.hover },
          "&.Mui-focusVisible": { outline: `2px solid ${VIOLET.main}`, outlineOffset: -2 },
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT.sans,
            fontSize: 12.5,
            color: NEUTRAL.textSecondary,
            fontVariantNumeric: "tabular-nums",
            display: { xs: "none", md: "block" },
          }}
        >
          {timeLabel(entry.created_at)}
        </Typography>
        <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1, minWidth: 0 }}>
          <UserAvatar name={actor} size={24} />
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontFamily: FONT.sans, fontSize: 13, color: NEUTRAL.text }}>
              {actor}
            </Typography>
            <Typography sx={{ fontFamily: FONT.sans, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", color: NEUTRAL.textMuted }}>
              {entry.actor_role || "—"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <ActionMark action={entry.action} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT.sans, fontSize: 13.5, color: NEUTRAL.text, lineHeight: 1.35, wordBreak: "break-word" }}>
            {entry.summary}
          </Typography>
          <Typography sx={{ fontFamily: FONT.sans, fontSize: 11.5, color: NEUTRAL.textMuted, mt: 0.25 }}>
            {entityTypeLabel(entry.entity_type)}
            {fieldCount > 0 ? ` · ${fieldCount} ${fieldCount === 1 ? "setting" : "settings"}` : ""}
            <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
              {` · ${timeLabel(entry.created_at)} · ${actor} (${entry.actor_role || "—"})`}
            </Box>
          </Typography>
        </Box>
        <KeyboardArrowDownIcon
          aria-hidden
          sx={{ color: NEUTRAL.textMuted, fontSize: 20, transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms ease" }}
        />
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <Box id={detailsId} sx={{ pr: { xs: 1, md: 2 }, pl: { xs: 1, md: "246px" }, pb: 1.5 }}>
          <Box
            sx={{
              border: `1px solid ${NEUTRAL.border}`,
              borderRadius: `${RADIUS.card}px`,
              bgcolor: NEUTRAL.surface,
              overflow: "hidden",
              pt: 0.75,
            }}
          >
            <ChangeTable changes={entry.changes} source={entry.change_source} />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
